import {SimulationsLayout} from "@/components/layouts/page/SimulationsLayout";

import {useEffect, useMemo, useState} from "react";

import {useLevelOrder} from "@hooks/LevelOrder";
import constrBanner from "@images/general/constr.png";
import {
  clearTable,
  getAllScenarios,
} from "@redux/slices/scenarioSlices/tableSlice";
import {BorderArticle} from "@ui/general/BorderArticle";
import {Spinner} from "@ui/general/Spinner";
import {WarningData} from "@ui/general/WarningData";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import styles from "./styles.module.scss";

export const CallPage = () => {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const {scenarioLevels, certificationLevels} = useLevelOrder();

  const [scenariosToDisplay, setScenariosToDisplay] = useState([]);

  const {status} = useSelector((state) => state.scenario.table);
  const {scenarios} = useSelector((state) => state.scenario.table.data);
  const {certifications} = useSelector((state) => state.scenario.table.data);

  const isDevEnvironment = useMemo(() => {
    return window.location.hostname.toLowerCase().includes("localhost");
  }, []);

  const showAvatarTestingBlock = useMemo(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return (
      window.location.hostname.toLowerCase().includes("ava-test.cosmart") ||
      window.location.hostname.toLowerCase().includes("ava-test.vivanti") ||
      window.location.hostname.toLowerCase().includes("localhost")
    );
  }, []);

  const AVATAR_TEST_SCENARIO_ID = isDevEnvironment ? 5 : 68;

  const isDueDatePassed = (due_date) => {
    if (!due_date) {
      return false;
    }

    const now = moment();
    const dueDate = moment(due_date);
    const diffDays = dueDate.diff(now, "days");

    return diffDays < -14;
  };

  const groupedScenarios = useMemo(() => {
    if (!scenarios || scenarios.length === 0) {
      return null;
    }

    return scenarios.reduce((groups, scenario) => {
      const productLine = scenario.product_line;

      if (!groups[productLine]) {
        groups[productLine] = [];
      }

      groups[productLine].push(scenario);

      return groups;
    }, {});
  }, [scenarios]);

  /** Данные из ответа `GET /api/v1/scenarios/simulations_allowed` (см. jb). */
  const avatarTestScenarioCard = useMemo(() => {
    if (!Array.isArray(scenarios)) {
      return null;
    }

    const row = scenarios.find(
      (s) => Number(s?.id) === AVATAR_TEST_SCENARIO_ID,
    );
    if (!row) {
      return null;
    }

    return {
      id: row.id,
      name: row.name,
      avatar_type: row.avatar_type,
      mask: row.mask,
      cover: row.cover,
      due_date: row.due_date,
    };
  }, [scenarios, AVATAR_TEST_SCENARIO_ID]);

  /**
   * Сценарии, которые показываем только в dev-блоке «Testing Avatars» —
   * в Certification / product lines их убираем, иначе везде одна и та же карточка.
   */
  const devExclusiveScenarioIds = useMemo(() => {
    if (!showAvatarTestingBlock || !avatarTestScenarioCard) {
      return null;
    }
    return new Set([Number(avatarTestScenarioCard.id)]);
  }, [showAvatarTestingBlock, avatarTestScenarioCard]);

  const visibleCertifications = useMemo(() => {
    if (!Array.isArray(certifications)) {
      return [];
    }
    return certifications.filter((certification) => {
      if (!certification) {
        return false;
      }
      if (isDueDatePassed(certification.finish_date)) {
        return false;
      }
      if (devExclusiveScenarioIds?.has(Number(certification.id))) {
        return false;
      }
      return true;
    });
  }, [certifications, devExclusiveScenarioIds]);

  const isLoading = status === "loading";
  const isError = status === "error";
  const isEmpty = scenariosToDisplay.length === 0;
  const hasCertifications = visibleCertifications.length > 0;
  const listIsEmpty = isEmpty && !hasCertifications;

  useEffect(() => {
    if (groupedScenarios) {
      setScenariosToDisplay(
        Object.entries(groupedScenarios)
          .map(([productLine, scenarios]) => ({
            id: productLine,
            name: productLine,
            scenarios: scenarios.filter((scenario) => {
              if (!scenario) {
                return false;
              }
              if (isDueDatePassed(scenario.due_date)) {
                return false;
              }
              if (devExclusiveScenarioIds?.has(Number(scenario.id))) {
                return false;
              }
              return true;
            }),
          }))
          .filter((group) => group.scenarios.length > 0)
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
    } else {
      setScenariosToDisplay([]);
    }
  }, [groupedScenarios, devExclusiveScenarioIds]);

  useEffect(() => {
    dispatch(getAllScenarios());

    return () => {
      dispatch(clearTable());
    };
  }, [dispatch]);

  return (
    <div className={"container-big"}>
      <div className={styles.call}>
        {isLoading ? (
          <Spinner
            width={80}
            height={80}
          />
        ) : isError ? (
          <div className={styles.callEmpty}>
            <BorderArticle>
              <WarningData type={"error"} />
            </BorderArticle>
          </div>
        ) : (
          <ul>
            {avatarTestScenarioCard && showAvatarTestingBlock && (
              <li key={"avatar-testing"}>
                <SimulationsLayout
                  title={t("page.simulations.tab.avatar_testing")}
                  background={"var(--greyLight)"}
                  levelList={scenarioLevels}
                  cards={[avatarTestScenarioCard]}
                  useAnamChat
                  footerBannerSrc={constrBanner}
                  footerBannerAlt={"construction"}
                  footerBannerAbsoluteFill
                />
              </li>
            )}

            {listIsEmpty && (
              <li>
                <div className={styles.callEmpty}>
                  <BorderArticle>
                    <WarningData type={"empty"} />
                  </BorderArticle>
                </div>
              </li>
            )}

            {hasCertifications && (
              <li>
                <SimulationsLayout
                  isBig
                  background={
                    "linear-gradient(135deg, #8692CB 0%, #B262C9 100%)"
                  }
                  levelList={certificationLevels}
                  cards={visibleCertifications}
                  title={
                    <span style={{color: "var(--white)"}}>
                      {t("page.simulations.tab.certification")}
                    </span>
                  }
                />
              </li>
            )}

            {scenariosToDisplay &&
              scenariosToDisplay.length > 0 &&
              scenariosToDisplay.map((item) => (
                <li key={item.id}>
                  <SimulationsLayout
                    title={item.name}
                    cards={item.scenarios}
                    levelList={scenarioLevels}
                  />
                </li>
              ))}
          </ul>
        )}
      </div>
    </div>
  );
};
